import { httpApi } from "api/http.api";
import { getSearchResult } from "api/search.api";
import moment from "moment";

export const camelize = (string: string): string => {
  return string
    .split(" ")
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)
    )
    .join("");
};

export const getDate = (date: string): string => {
  if (!date) return "";
  return moment(date).format("MM/DD/YYYY");
};

export const getCurrentDate = () => {
  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return formatter.format(date);
};

export const getCurrencyPrice = (price: number, currency: string): string => {
  price = price ? price : 0;
  switch (currency) {
    case "dollar": {
      return `$${price}`;
    }
    case "pound": {
      return `£${price}`;
    }
    case "euro": {
      return `€${price}`;
    }

    default: {
      return `$${price}`;
    }
  }
};

export const parseJwt = (token: string) => {
  if (!token) {
    return;
  }
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace("-", "+").replace("_", "/");
  return JSON.parse(window.atob(base64));
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string, alpha: string | number): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return alpha ? `rgba(${r}, ${g}, ${b}, ${alpha})` : `rgb(${r}, ${g}, ${b})`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : "100%";

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === "number"
    ? `${prop}px`
    : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

// export const defineColorByPriority = (priority: Priority, theme: DefaultTheme): string => {
//   switch (priority) {
//     case Priority.INFO:
//       return theme.colors.main.primary;
//     case Priority.LOW:
//       return theme.colors.main.success;
//     case Priority.MEDIUM:
//       return theme.colors.main.warning;
//     case Priority.HIGH:
//       return theme.colors.main.error;
//     default:
//       return theme.colors.main.success;
//   }
// };

// export const defineColorBySeverity = (severity: NotificationType | undefined, theme: DefaultTheme): string => {
//   switch (severity) {
//     case 'error':
//       return theme.colors.main.error;
//     case 'warning':
//       return theme.colors.main.warning;
//     case 'success':
//       return theme.colors.main.success;
//     case 'info':
//       return theme.colors.main.primary;
//     default:
//       return theme.colors.main.primary;
//   }
// };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const trimStr = (str: string, charCount = 15) => {
  if (!str) return "";
  const res = str.substring(0, charCount);
  return res + "...";
};

export const getDateAndTime = (date: string) => {
  if (!date) {
    return "";
  }
  const isoDate = date;
  const formattedDate = moment(isoDate).format("MMMM Do YYYY, h:mm a");
  return formattedDate;
};

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname;

export const findIndex = (array: any, key: any, value: any): number => {
  for (let index = 0; index < array.length; index++) {
    if (typeof array[index] === "string") {
      if (array[index] === value) {
        return index;
      }
    } else {
      if (array[index][key] === value) {
        return index;
      }
    }
  }
  return -1; // Element not found
};

export const getInitials = (name: string) => {
  // Split the name into an array of words
  const words = name.split(" ");

  // Initialize an empty string to store initials
  let initials = "";

  // Loop through each word and add the first character (initial) to the initials string
  for (let i = 0; i < words.length; i++) {
    initials += words[i][0];
  }

  // Convert the initials to uppercase and return them
  return initials.toUpperCase();
};

export const getRandomHexColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";

  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }

  return color;
};

export const formatName = (input: string): string => {
  if (!input) return "";
  // Split the input string by underscores and convert each word to lowercase
  const words = input.toLowerCase().split("_");

  // Capitalize the first letter of each word and join them with a space
  const formattedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  // Join the formatted words with a space to create the final formatted string
  const formattedName = formattedWords.join(" ");

  return formattedName;
};

export const getUniqueRecords = (data: any) => {
  const idSet = new Set();
  const uniqueRecords = [];

  for (const item of data) {
    if (!idSet.has(item.id)) {
      idSet.add(item.id);
      uniqueRecords.push(item);
    }
  }

  return uniqueRecords;
};

export const convertToKFormat = (number: number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return "0";
  }

  if (Math.abs(number) >= 1e6) {
    // If the number is in millions, convert it to millions format
    let millions = number / 1e6;
    millions = Math.round(millions * 10) / 10; // Round to one decimal place
    return millions + "M+";
  } else if (Math.abs(number) >= 1000) {
    // If the number is in thousands, convert it to thousands format
    let thousands = number / 1000;
    thousands = Math.round(thousands);
    return thousands + "K+";
  } else {
    // Otherwise, just return the number as is
    return number;
  }
};

export const formatFilterLabel = (field: string): string => {
  // Capitalize the first letter and replace underscores with spaces
  return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ");
};

export const getPages = (array: any[], itemsPerPage: number) => {
  // claculate the total number of pages
  const totalPages = Math.ceil(array?.length / itemsPerPage);

  // create an array to store page
  const pages: { page: number; content: any[] }[] = [];

  // Genrate each page
  for (let page = 1; page <= totalPages; page++) {
    // calculate the start and end index for the crrunt page
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // get the items for the crrunt page
    const pageItems = array?.slice(startIndex, endIndex);

    //add the page content to the array
    pages.push({ page, content: pageItems });
  }

  return pages;
};
export function addNumberField(obj: { number: any }, pageNumber: number, index: number) {
  obj.number = pageNumber * 20 + index + 1;
}

export function formatMessageText(messageText: string): string {
  const formattedMessage = messageText.replace(/\n/g, "\n");

  return formattedMessage;
}

export function convertToLocalDate(dateString: string) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export const fetchCompanies = async (query: string, setAllIndustries: any) => {
  try {
    const response = await getSearchResult(query);
    const data: any = response;
    setAllIndustries(
      data.map((item: any) => ({
        value: item,
        label: item,
      }))
    );
  } catch (err) {
    console.error(err);
  }
};

export const statusLabelAndColor = [
  {
    key: "OPEN",
    label: "Open",
    color: "green",
  },
  {
    key: "ON_HOLD",
    label: "On Hold",
    color: "orange",
  },
  {
    key: "COMPLETE",
    label: "Complete",
    color: "green",
  },
  {
    key: "CANCELLED",
    label: "Cancelled",
    color: "red",
  },
];

export const getStatusObject = (key: string) => {
  return statusLabelAndColor.find((item) => item.key === key);
};

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const formattedDate = date.toLocaleDateString("en-US", options);
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formattedDate}, ${formattedTime}`;
}

export function formatExperienceString(expString: string) {
  if (!expString) return "";

  const yearRegex = /(\d+)\s*y(?:ears?)?/i;
  const monthRegex = /(\d+)\s*m(?:onths?)?/i;

  const yearsMatch = expString.match(yearRegex);
  const monthsMatch = expString.match(monthRegex);

  const years = yearsMatch ? parseInt(yearsMatch[1]) : 0;
  const months = monthsMatch ? parseInt(monthsMatch[1]) : 0;

  let result = "";
  if (years) result += `${years} Y`;
  if (months) result += (result ? ", " : "") + `${months} M`;

  return result || "0 M";
}

export const getEditedField = (notes: any[], data: any, key: string, fallbackKey: string) => {
  const values =
    notes?.map((note: any) => note?.custom_fields?.[key])
      .filter((val: any) => key === "document" || Boolean(val)) || [];

  if (values.length > 0) return values[0];

  return data?.[fallbackKey] ?? (
    ["work_experience", "education_experience", "emails", "email", "phone_numbers", "other_profiles_urls"].includes(key)
      ? []
      : ""
  );
};

export const getEditedWorkEducationField = (notes: any[], data: any, key: string, fallbackKey: string) => {
  const noteValues = notes?.map((note: any) => note?.custom_fields?.[key]).filter(Boolean) || [];
  if (noteValues.length > 0) {
    return noteValues.flatMap((group: any) =>
      group.flatMap((item: any) => item?.[key] || [])
    );
  }
  return data?.[fallbackKey] ?? [];
};

export const updatedSingleCandidates = (notes: any, candidate: any) => {
  const updatedEmails = getEditedField(notes, candidate, "emails", "emails");
  const updatedPhones = getEditedField(notes, candidate, "phone_numbers", "phone_numbers");

  const addAsteriskToEmails = (emails: string[]) => {
    if (!Array.isArray(emails)) return emails;

    // Step 1: Filter notes that have emails inside custom_fields
    const notesWithEmails = notes.filter((note: any) =>
      Array.isArray(note?.custom_fields?.emails) && note.custom_fields.emails.length > 0
    );

    if (notesWithEmails.length === 0) return emails;

    // Step 2: Find the latest note based on modified_date
    const latestNote = notesWithEmails.reduce((latest: any, current: any) => {
      const latestDate = new Date(latest?.custom_fields.modified_date || 0);
      const currentDate = new Date(current?.custom_fields.modified_date || 0);
      return currentDate > latestDate ? current : latest;
    });

    // Step 3: Create a Set of emails for faster lookup
    const latestEmailsSet = new Set(
      (latestNote.custom_fields.emails || []).map((email: string) => email.toLowerCase())
    );

    // Step 4: Map over candidate emails and add asterisk if found in latest note's emails
    return emails.map((email) => {
      const emailFoundInLatestNote = latestEmailsSet.has(email.toLowerCase()) && !candidate?.emails?.includes(email.toLowerCase());
      return emailFoundInLatestNote ? `${email}*` : email;
    });
  };

  const addAsteriskToPhones = (phone_numbers: string[]) => {
    if (!Array.isArray(phone_numbers)) return phone_numbers;

    // Step 1: Filter notes that have emails inside custom_fields
    const notesWithPhones = notes.filter((note: any) =>
      Array.isArray(note?.custom_fields?.phone_numbers) && note.custom_fields.phone_numbers.length > 0
    );

    if (notesWithPhones.length === 0) return phone_numbers;

    // Find the latest note based on modified_date
    const latestNote = notesWithPhones.reduce((latest: any, current: any) => {
      const latestDate = new Date(latest?.custom_fields.modified_date || 0);
      const currentDate = new Date(current?.custom_fields.modified_date || 0);
      return currentDate > latestDate ? current : latest;
    }, null);

    // If no latest note found, return emails as is
    if (!latestNote?.custom_fields?.phone_numbers) return phone_numbers;

    // Create a Set for faster lookup
    const latestPhonesSet = new Set(
      latestNote.custom_fields.phone_numbers || []
    );

    return phone_numbers.map((phone) => {
      const phoneFoundInNotes = latestPhonesSet.has(phone) && !candidate?.phone_numbers?.includes(phone);
      return phoneFoundInNotes ? `${phone}*` : phone;
    });
  };

  return {
    ...candidate,
    full_name: getEditedField(notes, candidate, "display_name", "full_name"),
    titles: getEditedField(notes, candidate, "title", "titles"),
    location: getEditedField(notes, candidate, "location", "location"),
    bio: getEditedField(notes, candidate, "bio", "bio"),
    work_experience: getEditedField(notes, candidate, "work_experience", "work_experience"),
    document: getEditedField(notes, candidate, "document", "document"),
    emails: addAsteriskToEmails(updatedEmails),
    email: getEditedField(notes, candidate, "emails", "email"),
    phone_numbers: addAsteriskToPhones(updatedPhones),
    other_profiles_urls: getEditedField(notes, candidate, "other_profiles_urls", "other_profiles_urls"),
    education_experience: [...candidate?.education_experience || [], ...getEditedWorkEducationField(notes, candidate, "education_experience", "education_experience")]
  };
};


export const updatedCandidates = (candidates: any) => {

  const candidateList = candidates?.map((candidate: any) => {
    const notes = candidate?.notes || [];
    const result = {
      ...candidate,
      full_name: getEditedField(notes, candidate, "display_name", "full_name"),
      titles: getEditedField(notes, candidate, "title", "titles"),
      // title: getEditedField(notes, candidate, "title", "title"),
      location: getEditedField(notes, candidate, "location", "location"),
      bio: getEditedField(notes, candidate, "bio", "bio"),
      work_experience: getEditedField(notes, candidate, "work_experience", "work_experience"),
      document: getEditedField(notes, candidate, "document", "document"),
      email: getEditedField(notes, candidate, "emails", "emails"),
      emails: getEditedField(notes, candidate, "emails", "emails"),
      phone_numbers: getEditedField(notes, candidate, "phone_numbers", "phone_numbers"),
      other_profiles_urls: getEditedField(notes, candidate, "other_profiles_urls", "other_profiles_urls"),
      education_experience: getEditedWorkEducationField(notes, candidate, "education_experience", "education_experience")
    };
    return result;
  });

  return candidateList;
};

export function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export const getFileNameFromUrl = (url: string): string => {
  try {
    return url.split('/').pop() || '';
  } catch (error) {
    return '';
  }
};

export const requestMediaPermissions = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stream.getTracks().forEach(track => track.stop()); // stop immediately after checking
    return true;
  } catch (error) {
    console.error("Media permission error:", error);
    return false;
  }
};

export const convertImageToBase64 = async (imageUrl: string): Promise<string | null> => {
  try {
    if (!imageUrl || typeof imageUrl !== "string") {
      console.warn("Invalid image URL:", imageUrl);
      return null;
    }

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.setAttribute("crossorigin", "anonymous"); // optional but visible in DOM

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject("Canvas context not available");
            return;
          }

          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } catch (err) {
          reject("Canvas processing failed: " + err);
        }
      };

      img.onerror = (event) => {
        console.error("Image failed to load", {
          url: imageUrl,
          event,
        });
        reject(`Image failed to load: ${imageUrl}`);
      };

      img.src = imageUrl;
    });
  } catch (error) {
    console.error("Failed to convert image to base64:", error);
    return null;
  }
};


export const ArrayToString = (value: any) => {
  const result = Array.isArray(value) ? value.join('<br><br>') : String(value || "");
  return result;
};

export function isLinkedInOrDoximity(url: string): 'linkedin' | 'doximity' | 'unknown' {
  try {
    // Create a URL object to properly parse the input
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();

    // Check for LinkedIn URLs
    if (hostname.includes('linkedin.com')) {
      return 'linkedin';
    }
    // Check for Doximity URLs
    if (hostname.includes('doximity.com')) {
      return 'doximity';
    }

    return 'unknown';
  } catch (e) {
    // Handle invalid URLs
    return 'unknown';
  }
}

export const reportEnabledEmails = [
  "peter@sinecure.ai",
  "joel@sinecure.ai",
  "laura@strategysource.com",
  "erin.mastel@graceblue.com",
  "erin.mastel+prospect@graceblue.com",
  "jackie.haranzo@graceblue.com",
  "nabiha.khan@graceblue.com",
  "ardiana.bacaj@graceblue.com",
  "felagot.tilaye@graceblue.com",
  "helen.duffy@graceblue.com",
  "molly@sinecure.ai",
  "tracy@sinecure.ai",
  "uma@sinecure.ai"
];
export function convertStringToArray(inputString: string | null | undefined): string[] {
  // Handle null, undefined, or empty string
  if (inputString == null || inputString === '') {
    return [];
  }

  // Handle non-string inputs (though TypeScript should prevent this at compile time)
  if (typeof inputString !== 'string') {
    return [];
  }

  try {
    return inputString
      .replace(/^["'`\s]+|["'`\s]+$/g, '') // Remove surrounding quotes, backticks, and whitespace
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== ''); // Remove empty strings
  } catch (error) {
    console.error('Error converting string to array:', error);
    return [];
  }
}

export const getStepDetailsById = (stepId: number | string, stepsConfig: any) => {
  const details = stepsConfig.find((step: any) => step?.id === stepId);
  return details;
};

